import { useTable, List, ShowButton, EditButton } from "@refinedev/antd";
import { Table, Space, Tag, Button, Tooltip } from "antd";
import {
  EyeOutlined,
  EditOutlined,
  BarChartOutlined,
  EyeInvisibleOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import type { Incidence, IncidenceFilters as IIncidenceFilters } from "../../types";
import type { UserInfo } from "../../types/auth";
import { isEurosemillasScoped } from "../../types/auth";
import dayjs from "dayjs";
import { useState } from "react";
import { useGetIdentity } from "@refinedev/core";
import { IncidenceFilters } from "./components/IncidenceFilters";
import type { CrudFilters } from "@refinedev/core";
import { usePageTitle } from "../../hooks";

// user_type values that grant admin access — same as AdminRoute guard.
const ADMIN_USER_TYPES = [1, 2];

// Status enum mirror of `INCIDENCE_STATUS` (apps/audits_app/models/incidence.py).
const STATUS_OPEN = 1;
const STATUS_CLOSED = 2;
const STATUS_CANCELLED = 3;

// localStorage key for persisting the "ocultar cerradas" toggle so the
// preference survives page reloads.
const HIDE_CLOSED_STORAGE_KEY = "incidences:hideClosed";

/**
 * IncidenceList Component
 * Displays a list of all incidences with filtering and search capabilities
 */
export const IncidenceList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<IIncidenceFilters>({});

  // Solo los admin (user_type 1 ó 2) pueden editar una incidencia. El
  // resto solo ve los detalles. El backend ya valida en su lado; este
  // gate del botón es UX para no enseñar acciones imposibles.
  const { data: identity } = useGetIdentity<UserInfo>();
  const userType = identity?.app_user?.user_type;
  const isAdmin =
    userType !== undefined
    && ADMIN_USER_TYPES.includes(userType)
    && !isEurosemillasScoped(identity);

  // Toggle "ocultar cerradas" — por defecto ocultas para reducir ruido
  // en el listado del día a día. Persistimos la preferencia en
  // localStorage para que no haya que volver a togglear al recargar.
  // Carga perezosa para no leer del DOM en el server (refine SSR-safe).
  const [hideClosed, setHideClosed] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    const stored = window.localStorage.getItem(HIDE_CLOSED_STORAGE_KEY);
    // Default: ocultas. Solo si el usuario las desactivó explícitamente
    // (= guardó "false") las mostramos al recargar.
    return stored === null ? true : stored === "true";
  });

  const toggleHideClosed = () => {
    setHideClosed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(HIDE_CLOSED_STORAGE_KEY, String(next));
      } catch {
        // localStorage puede fallar en modo privado / quota llena;
        // el toggle sigue funcionando en memoria.
      }
      return next;
    });
  };

  // After the Incidence → Ticket unification, the list is sourced
  // from /api/v1/tickets/ with a permanent filter on
  // category.kind="audit" so only audit incidences show here.
  const { tableProps, setFilters: setTableFilters } = useTable<Incidence>({
    resource: "v1/tickets",
    sorters: {
      initial: [
        {
          field: "created",
          order: "desc",
        },
      ],
    },
    pagination: {
      pageSize: 15,
    },
    filters: {
      permanent: [
        { field: "category__kind", operator: "eq", value: "audit" },
        // Cuando el toggle está en "ocultar cerradas", añadimos un
        // filtro permanent `status in (open, cancelled)`. permanent
        // se reconcilia entre renders, así que el cambio del toggle
        // refresca la query automáticamente.
        //
        // El dataProvider local ignora `operator` y vuelca el filtro
        // como `?field=value` literal. Por eso usamos `status__in` y
        // pasamos un CSV — el TicketViewSet.get_queryset lo parsea
        // como `status__in=[…]` en el ORM.
        ...(hideClosed
          ? [
              {
                field: "status__in",
                operator: "in" as const,
                value: [STATUS_OPEN, STATUS_CANCELLED].join(","),
              },
            ]
          : []),
      ],
    },
    syncWithLocation: true,
  });

  const handleViewDetail = (record: Incidence) => {
    navigate(`/incidences/${record.id}`);
  };

  const handleEdit = (record: Incidence) => {
    navigate(`/incidences/edit/${record.id}`);
  };

  // Handle filter changes
  const handleFilterChange = (newFilters: IIncidenceFilters) => {
    setFilters(newFilters);

    // Convert filters to Refine CrudFilters format
    const crudFilters: CrudFilters = [];

    if (newFilters.search) {
      crudFilters.push({
        field: "q",
        operator: "contains",
        value: newFilters.search,
      });
    }

    // Field renames after the Ticket unification:
    //   status_incidence → status
    //   kind_incidence   → category
    if (newFilters.status_incidence !== undefined) {
      crudFilters.push({
        field: "status",
        operator: "eq",
        value: newFilters.status_incidence,
      });
    }

    if (newFilters.priority) {
      crudFilters.push({
        field: "priority",
        operator: "eq",
        value: newFilters.priority,
      });
    }

    if (newFilters.kind_incidence) {
      crudFilters.push({
        field: "category",
        operator: "eq",
        value: newFilters.kind_incidence,
      });
    }

    if (newFilters.assigned_to) {
      crudFilters.push({
        field: "assigned_to",
        operator: "eq",
        value: newFilters.assigned_to,
      });
    }

    if (newFilters.deadline_from) {
      crudFilters.push({
        field: "deadline_from",
        operator: "gte",
        value: newFilters.deadline_from,
      });
    }

    if (newFilters.deadline_to) {
      crudFilters.push({
        field: "deadline_to",
        operator: "lte",
        value: newFilters.deadline_to,
      });
    }

    setTableFilters(crudFilters, "replace");
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setFilters({});
    setTableFilters([], "replace");
  };

  // Get status display (translated)
  const getStatusDisplay = (status: number): string => {
    switch (status) {
      case 1:
        return t("incidence.status.open", "Abierta");
      case 2:
        return t("incidence.status.closed", "Cerrada");
      case 3:
        return t("incidence.status.cancelled", "Cancelada");
      default:
        return "-";
    }
  };

  // Get status tag color
  const getStatusColor = (status: number): string => {
    switch (status) {
      case 1:
        return "blue"; // Abierta
      case 2:
        return "green"; // Cerrada
      case 3:
        return "red"; // Cancelada
      default:
        return "default";
    }
  };

  // Get priority display (translated)
  const getPriorityDisplay = (priority: string): string => {
    switch (priority) {
      case "critical":
        return t("incidence.priority.critical", "Crítica");
      case "high":
        return t("incidence.priority.high", "Alta");
      case "medium":
        return t("incidence.priority.medium", "Media");
      case "low":
        return t("incidence.priority.low", "Baja");
      default:
        return "-";
    }
  };

  // Get priority tag color
  const getPriorityColor = (priority: string): string => {
    switch (priority) {
      case "critical":
        return "red";
      case "high":
        return "orange";
      case "medium":
        return "blue";
      case "low":
        return "default";
      default:
        return "default";
    }
  };

  return (
    <div className="incidences-list">
      <List
        canCreate={false}
        headerButtons={({ defaultButtons }) => (
          <>
            {defaultButtons}
            <Tooltip
              title={
                hideClosed
                  ? t(
                      "incidence.show_closed_tooltip",
                      "Incluir incidencias cerradas en el listado",
                    )
                  : t(
                      "incidence.hide_closed_tooltip",
                      "Ocultar las incidencias cerradas",
                    )
              }
            >
              <Button
                type="default"
                icon={
                  hideClosed ? <EyeOutlined /> : <EyeInvisibleOutlined />
                }
                onClick={toggleHideClosed}
              >
                {hideClosed
                  ? t("incidence.show_closed", "Mostrar cerradas")
                  : t("incidence.hide_closed", "Ocultar cerradas")}
              </Button>
            </Tooltip>
            <Button
              type="default"
              icon={<BarChartOutlined />}
              onClick={() => navigate("/incidences/regularized-surface-report")}
            >
              {t("incidence.regularized_surface_report", "Reporte de Superficie Regularizada")}
            </Button>
          </>
        )}
      >
        {/* Filters Section */}
        <IncidenceFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
        />

        {/* Table */}
        <Table {...tableProps} rowKey="id" scroll={{ x: 1620 }}>
          {/* dataIndexes keep their legacy names — the backend
              serializer exposes them as aliases over the unified
              Ticket model so the table renders unchanged. */}
          <Table.Column
            dataIndex="num_incidence"
            title={t("incidence.number", "Número")}
            sorter
            width={120}
            fixed="left"
          />
          <Table.Column
            dataIndex="producer_name"
            title={t("audit.producer", "Productor")}
            width={180}
            render={(value: string | null) => value || "-"}
          />
          <Table.Column
            dataIndex="audit_number"
            title={t("incidence.audit", "Auditoría")}
            width={150}
            render={(value: string | null) => value || "-"}
          />
          <Table.Column
            dataIndex="kind_incidence_name"
            title={t("incidence.type", "Tipo")}
            width={180}
          />
          <Table.Column
            dataIndex="status_incidence"
            title={t("incidence.status_label", "Estado")}
            sorter
            width={120}
            render={(status: number) => (
              <Tag color={getStatusColor(status)}>{getStatusDisplay(status)}</Tag>
            )}
          />
          <Table.Column
            dataIndex="priority"
            title={t("incidence.priority_label", "Prioridad")}
            sorter
            width={120}
            render={(priority: string) => (
              <Tag color={getPriorityColor(priority)}>{getPriorityDisplay(priority)}</Tag>
            )}
          />
          <Table.Column
            dataIndex="assigned_to_name"
            title={t("incidence.assigned_to", "Asignado a")}
            width={150}
            render={(value: string | null) => value || "-"}
          />
          <Table.Column
            dataIndex="deadline"
            title={t("incidence.deadline", "Fecha límite")}
            sorter
            width={120}
            render={(value: string | null) =>
              value ? dayjs(value).format("DD/MM/YYYY") : "-"
            }
          />
          <Table.Column
            dataIndex="created"
            title={t("incidence.created", "Fecha creación")}
            sorter
            width={130}
            render={(value: string) => dayjs(value).format("DD/MM/YYYY HH:mm")}
          />
          <Table.Column
            title={t("common.actions", "Acciones")}
            dataIndex="actions"
            // Sin EditButton la columna queda con solo el ShowButton —
            // el width adaptativo evita un hueco grande para no-admin.
            width={isAdmin ? 120 : 80}
            fixed="right"
            render={(_, record: Incidence) => (
              <Space>
                <ShowButton
                  hideText
                  size="small"
                  recordItemId={record.id}
                  icon={<EyeOutlined />}
                  onClick={() => handleViewDetail(record)}
                />
                {/* Editar restringido a admin. El backend ya valida
                    el permiso en su lado; esto es UX para no exponer
                    una acción que iba a devolver 403. */}
                {isAdmin && (
                  <EditButton
                    hideText
                    size="small"
                    recordItemId={record.id}
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(record)}
                  />
                )}
              </Space>
            )}
          />
        </Table>
      </List>
    </div>
  );
};
